/**
 * What a founder is told when an edit is refused (canon APP-005).
 *
 * Keyed on the **code**, for the reason `./bid-messages.ts` gives: core's own messages are
 * contract text written for every caller, and some of them describe conditions a founder
 * can do nothing about. The code is the stable part of the contract
 * (`contracts/API.md` § Seller deal edit shape § Refusals).
 *
 * One rule is specific to this surface. **Nothing here may suggest the edit partly
 * happened.** Core writes an edit inside one guarded statement, so a refusal means the deal
 * is exactly as it was — and a founder who believes a half-saved deal is on the board would
 * either edit it again on top of nothing or leave it alone when it needed fixing. Every
 * sentence below says the deal was not changed, or says nothing about it at all.
 */
import { isNetworkError } from "../api/client";
import type { ApiError } from "../api/client";

const MESSAGES: Record<string, string> = {
  // `409 DEAL_NOT_EDITABLE`. Canon § 15 separates a change request from a takedown:
  // reversing an operator's decision is the operator's call, not the founder's, so this
  // points at Dealbid rather than inviting a retry that will never work.
  DEAL_NOT_EDITABLE:
    "Dealbid has this deal on hold, so it can't be edited here. Nothing was changed. Contact Dealbid to discuss it.",
  // `409 DEAL_EDIT_CONFLICT`. The one refusal on this surface that is genuinely worth
  // retrying, and the only one that asks the founder to reload first.
  DEAL_EDIT_CONFLICT:
    "This deal's review state changed while you were editing it. Nothing was saved. Reload it and make your changes again.",
  NOT_FOUND: "Dealbid no longer has this deal on your account. Reload your desk.",
  // The whole-request sentence for a field refusal. Each offending box carries core's own
  // message beside it, so this only has to point at them.
  VALIDATION_FAILED: "Dealbid didn't accept some of these changes. The details are beside each one.",
  PAYLOAD_TOO_LARGE:
    "That's more text than Dealbid accepts for a deal. Shorten the longer fields and save again.",
  MALFORMED_RESPONSE:
    "Dealbid didn't answer as expected, so it isn't clear whether the change was saved. Reload the deal before editing it again.",
};

/** The generic. Honest about the two things actually known: it failed, and retrying is fair. */
const GENERIC = "Dealbid couldn't save those changes. Try again in a moment.";

const OFFLINE =
  "Dealbid couldn't be reached, so nothing was saved. Check your connection and try again.";

export function messageForDealEditError(error: ApiError): string {
  // A malformed body also carries a null status — the request completed and the answer was
  // unreadable — so the code is checked first. Telling a founder their connection dropped
  // when it did not would send them to restart a router that is working.
  if (error.code !== null) return MESSAGES[error.code] ?? GENERIC;
  return isNetworkError(error) ? OFFLINE : GENERIC;
}

